import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import useActiveStore from '../../../hooks/useActiveStore';
import { updateSelectedStore } from '../../../redux/reducers/cartReducer';

import {
  convertTimeString,
  convertTimeToWeekday,
  convertClosedWeekday,
  formatPhoneNumber,
  magicStrings,
  getStoreStatus,
} from '../utils';
import Button from '../../UI/Button/Button';

import { NearbyStore, StoreLogo as StoreLogoI } from '../../../types';
import {
  Data,
  StoreLogo,
  StoreAddress,
  StorePhone,
  Times,
  Time,
  Footer,
  GoogleLink,
  SelectBtn,
  AppointmentButtonWrapper,
} from './styles';

interface Props {
  origin: Record<string, unknown>;
  allStoreLogos: StoreLogoI[];
  storeToDisplay: NearbyStore;
  showSelectBtn: boolean;
  hideBookAppointment: boolean;
  hideDirections: boolean;
}

const StoreHours = ({
  origin,
  allStoreLogos,
  storeToDisplay,
  showSelectBtn,
  hideBookAppointment,
  hideDirections,
}: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isTempClosed, setIsTempClosed] = useState(false);
  const { activeStore } = useActiveStore();
  const isActive = activeStore === storeToDisplay;
  const storeLogo = allStoreLogos.find(
    ({ storeName }) => storeName.toLowerCase() === activeStore.storeBanner?.name.toLowerCase(),
  );

  const tempClosedCopy = intl.locale === "en" ? 'Temporarily Closed'
    : 'Fermé temporairement';

  useEffect(() => {
    if (activeStore && activeStore.commonInfo.businessHours.length > 0) {
      const status = getStoreStatus(
        activeStore.commonInfo.businessHours[0].openingTime,
        activeStore.commonInfo.businessHours[0].closingTime,
      );
      setIsOpen(!!status && !activeStore.commonInfo.businessHours[0].closed);
      setIsTempClosed(false);
    } else {
      setIsTempClosed(true);
    }
  }, [activeStore]);

  const storeAddress = storeToDisplay && `${storeToDisplay.commonInfo.address.line1}, ${storeToDisplay.commonInfo.address.city}, ${storeToDisplay.commonInfo.address.province} `;
  return (
    <Data>
      {activeStore && (
        <>
          <StoreLogo>
            <img
              src={storeLogo?.asset?.localImage?.publicURL}
              alt={storeLogo?.altText}
            />
          </StoreLogo>
          <StoreAddress>
            {storeAddress}
            {storeToDisplay.commonInfo.address.postalCode}
          </StoreAddress>
          <StorePhone href={`tel:${activeStore.commonInfo.contactInfo.tel
            ?? storeToDisplay.commonInfo.contactInfo.tel}`}
          >
            {formatPhoneNumber(activeStore.commonInfo.contactInfo.tel
              ?? storeToDisplay.commonInfo.contactInfo.tel)}
          </StorePhone>
          <Times>
            {/* TMS Dept not returning businessHours for the day/week */}
            {isTempClosed && storeToDisplay.commonInfo.businessHours.map(
              ({ nameOfDay }, index) => {
                // Return current day of week
                if (index === 0) {
                  return (
                    <li key={uuidv4()}>
                      <Time className="not-active">
                        <p>
                          <span className="weekday">
                            {convertClosedWeekday(nameOfDay, intl.locale)}
                          </span>
                          {' '}
                          <span className="time">
                            {tempClosedCopy}
                          </span>
                        </p>
                      </Time>
                    </li>
                  );
                }
                return null;
              },
            )}
            {/* TMS Dept is returning businessHours for the day/week */}
            {!isTempClosed && activeStore.commonInfo.businessHours.map(
              ({ closingTime, openingTime, nameOfDay, closed }, index) => {
                // Return current day of week
                if (index === 0) {
                  return ((closingTime && openingTime) && !closed) ? (
                    <li key={convertTimeToWeekday(openingTime, intl.locale)}>
                      <Time>
                        <p>
                          <span className="weekday">
                            {convertTimeToWeekday(openingTime, intl.locale)}
                          </span>
                          {' '}
                          {isOpen ? (
                            <span className="time">
                              <strong className="green">
                                {magicStrings.storeListingTitleOpen(
                                  intl.locale,
                                )}
                              </strong>
                              {' '}
                              -
                              {' '}
                              {magicStrings.storeListingTitleCloses(
                                intl.locale,
                              )}
                              {' '}
                              {convertTimeString(closingTime, intl.locale)}
                            </span>
                          ) : (
                            <span className="time">
                              {convertTimeString(openingTime, intl.locale)}
                                &nbsp;-&nbsp;
                              {convertTimeString(closingTime, intl.locale)}
                            </span>
                          )}
                        </p>
                      </Time>
                    </li>
                  ) : (
                    <li key={uuidv4()}>
                      <Time className="not-active">
                        <p>
                          <span className="weekday">
                            {convertClosedWeekday(nameOfDay, intl.locale)}
                          </span>
                          {' '}
                          <span className="time">
                            {isTempClosed ? tempClosedCopy
                              : magicStrings.storeListingTitleClosedAllDay(
                                intl.locale,
                              )}
                          </span>
                        </p>
                      </Time>
                    </li>
                  );
                }
                // Return remaining days of week
                return ((closingTime && openingTime) && !closed && !isTempClosed) ? (
                  <li key={convertTimeToWeekday(openingTime, intl.locale)}>
                    <Time className="not-active">
                      <p>
                        <span className="weekday">
                          {convertTimeToWeekday(openingTime, intl.locale)}
                        </span>
                        {' '}
                        <span className="time">
                          {convertTimeString(openingTime, intl.locale)}
                            &nbsp;-&nbsp;
                          {convertTimeString(closingTime, intl.locale)}
                        </span>
                      </p>
                    </Time>
                  </li>
                ) : (
                  <li key={uuidv4()}>
                    <Time className="not-active">
                      <p>
                        <span className="weekday">
                          {convertClosedWeekday(nameOfDay, intl.locale)}
                        </span>
                        {' '}
                        <span className="time">
                          {isTempClosed ? tempClosedCopy
                            : magicStrings.storeListingTitleClosedAllDay(
                              intl.locale,
                            )}
                        </span>
                      </p>
                    </Time>
                  </li>
                );
              },
            )}
          </Times>
          <Footer>
            {showSelectBtn && (
              <SelectBtn
                type="button"
                className={isActive ? 'active' : ''}
                onClick={() => dispatch(updateSelectedStore(activeStore))}
              >
                {isActive
                  ? magicStrings.selected(intl.locale)
                  : magicStrings.selectThisStore(intl.locale)}
              </SelectBtn>
            )}
            {!hideBookAppointment && (
              <AppointmentButtonWrapper>
                <Button
                  buttonText={intl.formatMessage({
                    id: 'AppointmentButtonLabel',
                  })}
                  buttonColour="red-to-white"
                  buttonSize="slim"
                  url="/appointment"
                  route="internal"
                  locale={intl.locale}
                  state={{ requestedStore: activeStore }}
                />
              </AppointmentButtonWrapper>
            )}
            {!hideDirections && (
              <GoogleLink
                href={`https://www.google.com/maps/dir/?api=1&origin=${origin.lat},${origin.lng}&destination=${storeToDisplay.commonInfo.address.latitude},${storeToDisplay.commonInfo.address.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {magicStrings.getDirectionsLabel(intl.locale)}
              </GoogleLink>
            )}
          </Footer>
        </>
      )}
    </Data>
  );
};

export default StoreHours;
